export interface Accommodation {
  id: string;
  businessType: string;
  businessName: string;
  ownerName: string;
  email: string;
  phone: string;
  city: string;
  district: string;
  address: string;
  starRating?: string;
  roomCount?: string;
  priceRange: string;
  website?: string;
  instagram?: string;
  shortDesc: string;
  description: string;
  amenities: string[];
  checkIn?: string;
  checkOut?: string;
  cancellationPolicy?: string;
  languages?: string[];
  status: string;
  appliedAt: string;
  lat?: number;
  lng?: number;
  images?: string[];
  membershipTier?: "gold" | "free";
  goldSince?: string;
  goldUntil?: string;
  viewCount?: number;
  addedAt?: string;
  trending?: boolean;
}

export const typeLabels: Record<string, string> = {
  hotel: "Otel",
  pansiyon: "Pansiyon",
  butik: "Butik Otel",
  apart: "Apart",
  kamp: "Kamp & Glamping",
  firma: "Turizm Firması",
  bungalov: "Bungalov",
  karavan: "Karavan & Kamp",
};

export const typeColors: Record<string, string> = {
  hotel: "bg-amber-100 text-amber-700",
  pansiyon: "bg-emerald-100 text-emerald-700",
  butik: "bg-rose-100 text-rose-700",
  apart: "bg-sky-100 text-sky-700",
  kamp: "bg-lime-100 text-lime-700",
  firma: "bg-stone-100 text-stone-700",
  bungalov: "bg-teal-100 text-teal-700",
  karavan: "bg-orange-100 text-orange-700",
};

export const typeImages: Record<string, string[]> = {
  bungalov: [
    "https://readdy.ai/api/search-image?query=cozy%20wooden%20bungalow%20cabin%20forest%20lake%20Sapanca%20Kartepe%20Turkey%20nature%20green%20trees%20misty%20morning%20serene&width=900&height=600&seq=acc-bung-1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=bungalow%20interior%20wooden%20warm%20cozy%20fireplace%20mountain%20view%20Turkey%20nature%20cabin%20bedroom&width=900&height=600&seq=acc-bung-2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=bungalow%20terrace%20deck%20forest%20lake%20view%20Turkey%20morning%20coffee%20nature%20peaceful&width=900&height=600&seq=acc-bung-3&orientation=landscape",
    "https://readdy.ai/api/search-image?query=bungalow%20village%20Turkey%20mountain%20forest%20snow%20winter%20cozy%20wooden%20cabins%20scenic&width=900&height=600&seq=acc-bung-4&orientation=landscape",
  ],
  karavan: [
    "https://readdy.ai/api/search-image?query=caravan%20camping%20site%20Turkey%20nature%20forest%20green%20meadow%20tents%20caravans%20scenic%20outdoor%20adventure&width=900&height=600&seq=acc-kara-1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=caravan%20interior%20cozy%20modern%20mobile%20home%20Turkey%20camping%20outdoor%20lifestyle&width=900&height=600&seq=acc-kara-2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=camping%20site%20Turkey%20lake%20forest%20campfire%20evening%20stars%20outdoor%20nature%20adventure&width=900&height=600&seq=acc-kara-3&orientation=landscape",
    "https://readdy.ai/api/search-image?query=caravan%20park%20Turkey%20sea%20coast%20outdoor%20camping%20summer%20nature%20scenic&width=900&height=600&seq=acc-kara-4&orientation=landscape",
  ],
  hotel: [
    "https://readdy.ai/api/search-image?query=luxury%20hotel%20lobby%20interior%20elegant%20warm%20lighting%20Turkey%20boutique%20accommodation%20grand%20entrance%20marble%20floor&width=900&height=600&seq=acc-hotel-1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=luxury%20hotel%20room%20interior%20elegant%20bed%20white%20linen%20sea%20view%20balcony%20Turkey&width=900&height=600&seq=acc-hotel-2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=hotel%20swimming%20pool%20outdoor%20Turkey%20Mediterranean%20resort%20summer%20blue%20water&width=900&height=600&seq=acc-hotel-3&orientation=landscape",
    "https://readdy.ai/api/search-image?query=hotel%20restaurant%20breakfast%20buffet%20elegant%20interior%20Turkey%20morning%20light&width=900&height=600&seq=acc-hotel-4&orientation=landscape",
  ],
  pansiyon: [
    "https://readdy.ai/api/search-image?query=cozy%20Turkish%20pansiyon%20guesthouse%20traditional%20wooden%20interior%20warm%20homey%20atmosphere%20stone%20walls&width=900&height=600&seq=acc-pan-1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Turkish%20guesthouse%20room%20cozy%20bed%20traditional%20decor%20warm%20colors%20wooden%20furniture&width=900&height=600&seq=acc-pan-2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Turkish%20breakfast%20traditional%20spread%20pansiyon%20morning%20table%20cheese%20olives%20honey&width=900&height=600&seq=acc-pan-3&orientation=landscape",
    "https://readdy.ai/api/search-image?query=pansiyon%20garden%20terrace%20Turkey%20flowers%20cozy%20outdoor%20seating%20morning%20coffee&width=900&height=600&seq=acc-pan-4&orientation=landscape",
  ],
  butik: [
    "https://readdy.ai/api/search-image?query=boutique%20hotel%20Turkey%20stone%20cave%20room%20elegant%20design%20Cappadocia%20unique%20accommodation%20carved%20rock&width=900&height=600&seq=acc-but-1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=boutique%20hotel%20suite%20luxury%20bathroom%20stone%20walls%20Turkey%20unique%20design%20spa&width=900&height=600&seq=acc-but-2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=boutique%20hotel%20terrace%20view%20Cappadocia%20Turkey%20hot%20air%20balloons%20morning%20breakfast&width=900&height=600&seq=acc-but-3&orientation=landscape",
    "https://readdy.ai/api/search-image?query=boutique%20hotel%20lounge%20fireplace%20cozy%20stone%20interior%20Turkey%20winter%20warm%20atmosphere&width=900&height=600&seq=acc-but-4&orientation=landscape",
  ],
  apart: [
    "https://readdy.ai/api/search-image?query=modern%20apartment%20Turkey%20holiday%20rental%20clean%20interior%20sea%20view%20living%20room&width=900&height=600&seq=acc-ap-1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=apartment%20kitchen%20modern%20Turkey%20holiday%20rental%20fully%20equipped%20clean%20bright&width=900&height=600&seq=acc-ap-2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=apartment%20bedroom%20Turkey%20holiday%20rental%20comfortable%20bed%20white%20linen%20natural%20light&width=900&height=600&seq=acc-ap-3&orientation=landscape",
    "https://readdy.ai/api/search-image?query=apartment%20balcony%20Turkey%20sea%20view%20sunset%20holiday%20rental%20outdoor%20furniture&width=900&height=600&seq=acc-ap-4&orientation=landscape",
  ],
  kamp: [
    "https://readdy.ai/api/search-image?query=glamping%20Turkey%20mountain%20nature%20luxury%20tent%20camping%20green%20forest%20scenic%20Kackar&width=900&height=600&seq=acc-kamp-1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=glamping%20tent%20interior%20luxury%20bed%20nature%20Turkey%20forest%20mountain%20cozy%20warm&width=900&height=600&seq=acc-kamp-2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=camping%20fire%20outdoor%20Turkey%20nature%20mountain%20evening%20stars%20forest%20atmosphere&width=900&height=600&seq=acc-kamp-3&orientation=landscape",
    "https://readdy.ai/api/search-image?query=glamping%20breakfast%20outdoor%20nature%20Turkey%20morning%20mountain%20view%20organic%20food&width=900&height=600&seq=acc-kamp-4&orientation=landscape",
  ],
  firma: [
    "https://readdy.ai/api/search-image?query=Turkey%20travel%20agency%20tour%20company%20professional%20tourism%20service%20group%20tour%20bus&width=900&height=600&seq=acc-firm-1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Turkey%20tour%20guide%20group%20tourists%20Cappadocia%20cultural%20heritage%20sightseeing&width=900&height=600&seq=acc-firm-2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Turkey%20boat%20tour%20Aegean%20sea%20blue%20cruise%20group%20travel%20summer&width=900&height=600&seq=acc-firm-3&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Turkey%20hot%20air%20balloon%20tour%20Cappadocia%20sunrise%20group%20travel%20adventure&width=900&height=600&seq=acc-firm-4&orientation=landscape",
  ],
};

export const amenityIcons: Record<string, string> = {
  "Ücretsiz WiFi": "ri-wifi-line",
  "Otopark": "ri-parking-line",
  "Havuz": "ri-water-flash-line",
  "Spa & Masaj": "ri-heart-pulse-line",
  "Restoran": "ri-restaurant-line",
  "Bar": "ri-goblet-line",
  "Kahvaltı Dahil": "ri-cup-line",
  "Klima": "ri-temp-cold-line",
  "Balkon / Teras": "ri-home-smile-line",
  "Deniz Manzarası": "ri-ship-line",
  "Dağ Manzarası": "ri-landscape-line",
  "Evcil Hayvan Kabul": "ri-bear-smile-line",
  "Engelli Erişimi": "ri-wheelchair-line",
  "Çamaşırhane": "ri-t-shirt-line",
  "Bisiklet Kiralama": "ri-bike-line",
  "Transfer Hizmeti": "ri-taxi-line",
  "Çocuk Oyun Alanı": "ri-gamepad-line",
  "Fitness Salonu": "ri-run-line",
  "Bahçe / Teras": "ri-plant-line",
};

export const seedAccommodations: Accommodation[] = [
  {
    id: "kapadokya-cave-suites",
    businessType: "butik",
    businessName: "Kapadokya Cave Suites",
    ownerName: "Mehmet Yılmaz",
    email: "info@kapadokyacave.com",
    phone: "0384 271 22 00",
    city: "Nevşehir (Kapadokya)",
    district: "Göreme",
    address: "Aydınlı Mah. No:12, Göreme, Nevşehir",
    starRating: "4",
    roomCount: "18",
    priceRange: "₺₺₺ (2500-5000₺)",
    website: "https://kapadokyacave.com",
    instagram: "@kapadokyacave",
    shortDesc: "Göreme'nin kalbinde, peri bacalarına bakan eşsiz kaya oyma oda deneyimi.",
    description: "Kapadokya'nın büyülü atmosferinde, tarihi kaya oyma odalarda konaklayın. Her odamız özgün tasarımıyla misafirlerimize unutulmaz bir deneyim sunmaktadır. Sabah kahvaltımız teras üzerinde, balon turlarının eşliğinde servis edilmektedir. Göreme'nin merkezinde yer alan otelimiz, tüm tarihi noktalara yürüme mesafesindedir.",
    amenities: ["Ücretsiz WiFi", "Kahvaltı Dahil", "Balkon / Teras", "Klima", "Transfer Hizmeti"],
    checkIn: "14:00",
    checkOut: "12:00",
    cancellationPolicy: "Girişten 48 saat öncesine kadar ücretsiz iptal",
    languages: ["Türkçe", "İngilizce", "Almanca"],
    status: "approved",
    appliedAt: "2026-03-25T10:00:00Z",
    lat: 38.6431,
    lng: 34.8289,
    images: [],
    membershipTier: "gold",
    goldSince: "2026-01-01T00:00:00Z",
    goldUntil: "2027-01-01T00:00:00Z",
    viewCount: 3842,
    addedAt: "2026-03-25T10:00:00Z",
    trending: true,
  },
  {
    id: "mardin-tas-ev-pansiyon",
    businessType: "pansiyon",
    businessName: "Mardin Taş Ev Pansiyon",
    ownerName: "Ayşe Demir",
    email: "info@mardintasev.com",
    phone: "0482 212 33 44",
    city: "Mardin",
    district: "Merkez",
    address: "Birinci Caddesi No:45, Mardin",
    roomCount: "8",
    priceRange: "₺₺ (1000-2500₺)",
    instagram: "@mardintasev",
    shortDesc: "Mardin'in tarihi taş evlerinde otantik bir konaklama deneyimi.",
    description: "Mardin'in eşsiz mimarisini yansıtan taş evimizde, Mezopotamya ovasına bakan muhteşem manzarayla uyandığınızda kendinizi tarihin içinde hissedeceksiniz. Geleneksel Mardin kahvaltısı ve ev yapımı lezzetler sunuyoruz. Her odamız özgün taş işçiliğiyle dekore edilmiştir.",
    amenities: ["Ücretsiz WiFi", "Kahvaltı Dahil", "Dağ Manzarası"],
    checkIn: "13:00",
    checkOut: "11:00",
    cancellationPolicy: "Girişten 24 saat öncesine kadar ücretsiz iptal",
    languages: ["Türkçe", "İngilizce", "Arapça"],
    status: "approved",
    appliedAt: "2026-04-02T09:00:00Z",
    lat: 37.3212,
    lng: 40.7245,
    images: [],
    membershipTier: "gold",
    goldSince: "2026-02-01T00:00:00Z",
    goldUntil: "2027-02-01T00:00:00Z",
    viewCount: 2156,
    addedAt: "2026-04-02T09:00:00Z",
    trending: true,
  },
  {
    id: "oludeniz-blue-bay-hotel",
    businessType: "hotel",
    businessName: "Ölüdeniz Blue Bay Hotel",
    ownerName: "Can Arslan",
    email: "info@bluebayoludeniz.com",
    phone: "0252 617 00 55",
    city: "Muğla (Bodrum/Marmaris)",
    district: "Ölüdeniz / Fethiye",
    address: "Belcekız Mah. Sahil Cad. No:8, Ölüdeniz",
    starRating: "4",
    roomCount: "42",
    priceRange: "₺₺₺ (2500-5000₺)",
    website: "https://bluebayoludeniz.com",
    instagram: "@bluebayoludeniz",
    shortDesc: "Ölüdeniz'in turkuaz sularına sıfır, yamaç paraşütü manzaralı butik otel.",
    description: "Türkiye'nin en güzel plajlarından Ölüdeniz'de, denize sıfır konumuyla misafirlerimize eşsiz bir tatil deneyimi sunuyoruz. Açık havuz, plaj barımız ve deniz sporları merkezimizle tam bir tatil cenneti. Tüm odalarımızdan deniz veya dağ manzarası görülmektedir.",
    amenities: ["Ücretsiz WiFi", "Havuz", "Restoran", "Bar", "Deniz Manzarası", "Klima", "Otopark"],
    checkIn: "14:00",
    checkOut: "12:00",
    cancellationPolicy: "Girişten 72 saat öncesine kadar ücretsiz iptal",
    languages: ["Türkçe", "İngilizce", "Rusça", "Almanca"],
    status: "approved",
    appliedAt: "2026-04-05T11:00:00Z",
    lat: 36.5500,
    lng: 29.1167,
    images: [],
    membershipTier: "free",
    viewCount: 4721,
    addedAt: "2026-04-05T11:00:00Z",
    trending: true,
  },
  {
    id: "safranbolu-konak-otel",
    businessType: "butik",
    businessName: "Safranbolu Konak Otel",
    ownerName: "Fatma Kaya",
    email: "info@safrankonakhotel.com",
    phone: "0370 712 55 66",
    city: "Karabük",
    district: "Safranbolu",
    address: "Çeşme Mah. Hükümet Sok. No:3, Safranbolu",
    starRating: "3",
    roomCount: "12",
    priceRange: "₺₺ (1000-2500₺)",
    website: "https://safrankonakhotel.com",
    shortDesc: "UNESCO Dünya Mirası Safranbolu'da tarihi Osmanlı konağında konaklama.",
    description: "200 yıllık tarihi Osmanlı konağında restore edilmiş odalarımızda, geleneksel Türk mimarisini yaşayın. Ahşap işlemeli tavanlar, geniş cumbalı odalar ve özgün bahçemizle sizi geçmişe götürüyoruz. Safranbolu'nun tarihi çarşısına yürüme mesafesindeyiz.",
    amenities: ["Ücretsiz WiFi", "Kahvaltı Dahil", "Bahçe / Teras", "Otopark"],
    checkIn: "14:00",
    checkOut: "11:00",
    cancellationPolicy: "Girişten 48 saat öncesine kadar ücretsiz iptal",
    languages: ["Türkçe", "İngilizce"],
    status: "approved",
    appliedAt: "2026-02-10T08:00:00Z",
    lat: 41.2497,
    lng: 32.6936,
    images: [],
    viewCount: 1893,
    addedAt: "2026-02-10T08:00:00Z",
  },
  {
    id: "ayder-doga-glamping",
    businessType: "kamp",
    businessName: "Ayder Doğa Glamping",
    ownerName: "Hasan Tekin",
    email: "info@ayderglamping.com",
    phone: "0464 657 22 33",
    city: "Rize",
    district: "Ayder Yaylası",
    address: "Ayder Yaylası, Çamlıhemşin, Rize",
    roomCount: "15",
    priceRange: "₺₺ (1000-2500₺)",
    instagram: "@ayderglamping",
    shortDesc: "Kaçkar Dağları eteklerinde, şelale sesi eşliğinde lüks çadır deneyimi.",
    description: "Ayder Yaylası'nın eşsiz doğasında, Kaçkar Dağları manzarasıyla lüks glamping deneyimi yaşayın. Isıtmalı çadırlarımız, organik kahvaltımız ve doğa yürüyüşü rehberlik hizmetimizle unutulmaz bir tatil sizi bekliyor. Şelale sesi eşliğinde uyumak paha biçilemez.",
    amenities: ["Kahvaltı Dahil", "Dağ Manzarası", "Bisiklet Kiralama"],
    checkIn: "15:00",
    checkOut: "11:00",
    cancellationPolicy: "Girişten 48 saat öncesine kadar ücretsiz iptal",
    languages: ["Türkçe", "İngilizce"],
    status: "approved",
    appliedAt: "2026-02-15T10:00:00Z",
    lat: 40.9167,
    lng: 41.0833,
    images: [],
    viewCount: 987,
    addedAt: "2026-02-15T10:00:00Z",
  },
  {
    id: "sapanca-orman-bungalovlari",
    businessType: "bungalov",
    businessName: "Sapanca Orman Bungalovları",
    ownerName: "Kemal Aydın",
    email: "info@sapancabungalov.com",
    phone: "0264 582 11 44",
    city: "Sakarya",
    district: "Sapanca",
    address: "Kırkpınar Mah. Orman Yolu No:7, Sapanca, Sakarya",
    roomCount: "12",
    priceRange: "₺₺ (1000-2500₺)",
    website: "https://sapancabungalov.com",
    instagram: "@sapancabungalov",
    shortDesc: "Sapanca Gölü kıyısında, çam ormanları içinde huzurlu bungalov tatili.",
    description: "Sapanca Gölü'nün eşsiz manzarasına karşı, çam ormanlarının ortasında konumlanan bungalovlarımızda doğayla iç içe bir tatil sizi bekliyor. Her bungalov özel terası, şöminesi ve tam donanımlı mutfağıyla konforlu bir konaklama sunmaktadır. İstanbul'a sadece 1.5 saat mesafede, hafta sonu kaçamağı için ideal.",
    amenities: ["Ücretsiz WiFi", "Otopark", "Bahçe / Teras", "Kahvaltı Dahil", "Dağ Manzarası", "Evcil Hayvan Kabul"],
    checkIn: "14:00",
    checkOut: "11:00",
    cancellationPolicy: "Girişten 48 saat öncesine kadar ücretsiz iptal",
    languages: ["Türkçe", "İngilizce"],
    status: "approved",
    appliedAt: "2026-04-01T10:00:00Z",
    lat: 40.6897,
    lng: 30.2672,
    images: [],
    membershipTier: "gold",
    goldSince: "2026-03-01T00:00:00Z",
    goldUntil: "2027-03-01T00:00:00Z",
    viewCount: 2340,
    addedAt: "2026-04-01T10:00:00Z",
    trending: true,
  },
  {
    id: "kartepe-dag-bungalovlari",
    businessName: "Kartepe Dağ Bungalovları",
    businessType: "bungalov",
    ownerName: "Selin Çelik",
    email: "info@kartepebungalov.com",
    phone: "0262 373 55 88",
    city: "Kocaeli",
    district: "Kartepe",
    address: "Kartepe Mahallesi Dağ Yolu No:14, Kartepe, Kocaeli",
    roomCount: "8",
    priceRange: "₺₺₺ (2500-5000₺)",
    instagram: "@kartepebungalov",
    shortDesc: "Kartepe'nin karlı zirvelerinde, İzmit Körfezi manzaralı lüks bungalov deneyimi.",
    description: "Kartepe Kayak Merkezi'ne 5 dakika mesafede, İzmit Körfezi'nin muhteşem manzarasına karşı konumlanan lüks bungalovlarımız kış ve yaz tatili için mükemmel bir seçenek. Jakuzili banyolar, panoramik cam cepheler ve özel barbekü alanlarıyla donatılmış bungalovlarımızda unutulmaz anlar yaşayın.",
    amenities: ["Ücretsiz WiFi", "Otopark", "Spa & Masaj", "Balkon / Teras", "Dağ Manzarası", "Kahvaltı Dahil", "Bisiklet Kiralama"],
    checkIn: "15:00",
    checkOut: "12:00",
    cancellationPolicy: "Girişten 72 saat öncesine kadar ücretsiz iptal",
    languages: ["Türkçe", "İngilizce"],
    status: "approved",
    appliedAt: "2026-03-20T09:00:00Z",
    lat: 40.7012,
    lng: 29.6789,
    images: [],
    membershipTier: "gold",
    goldSince: "2026-02-15T00:00:00Z",
    goldUntil: "2027-02-15T00:00:00Z",
    viewCount: 1876,
    addedAt: "2026-03-20T09:00:00Z",
    trending: true,
  },
  {
    id: "abant-gol-kenari-bungalov",
    businessName: "Abant Göl Kenarı Bungalov",
    businessType: "bungalov",
    ownerName: "Murat Yıldız",
    email: "info@abantbungalov.com",
    phone: "0374 224 33 11",
    city: "Bolu",
    district: "Abant",
    address: "Abant Tabiat Parkı Girişi No:3, Bolu",
    roomCount: "10",
    priceRange: "₺₺ (1000-2500₺)",
    instagram: "@abantbungalov",
    shortDesc: "Abant Gölü kıyısında, tabiat parkı içinde romantik bungalov tatili.",
    description: "Türkiye'nin en güzel tabiat parklarından Abant'ta, göl kıyısında konumlanan bungalovlarımızda doğanın kucağında dinlenin. Sabah sislerin arasında göl manzarası, akşam ateş başında sohbet... Çiftler ve aileler için ideal bir kaçamak noktası.",
    amenities: ["Ücretsiz WiFi", "Otopark", "Bahçe / Teras", "Bisiklet Kiralama", "Evcil Hayvan Kabul"],
    checkIn: "14:00",
    checkOut: "11:00",
    cancellationPolicy: "Girişten 48 saat öncesine kadar ücretsiz iptal",
    languages: ["Türkçe"],
    status: "approved",
    appliedAt: "2026-02-28T08:00:00Z",
    lat: 40.7333,
    lng: 31.4667,
    images: [],
    membershipTier: "free",
    viewCount: 943,
    addedAt: "2026-02-28T08:00:00Z",
  },
  {
    id: "sapanca-karavan-kamp-parki",
    businessType: "karavan",
    businessName: "Sapanca Karavan & Kamp Parkı",
    ownerName: "Ahmet Demir",
    email: "info@sapancakaravan.com",
    phone: "0264 591 22 77",
    city: "Sakarya",
    district: "Sapanca",
    address: "Gölkent Mah. Sahil Yolu No:22, Sapanca, Sakarya",
    roomCount: "30",
    priceRange: "₺ (500-1000₺)",
    website: "https://sapancakaravan.com",
    instagram: "@sapancakaravan",
    shortDesc: "Sapanca Gölü kıyısında tam donanımlı karavan parkı ve kamp alanı.",
    description: "Sapanca Gölü'nün hemen kıyısında, elektrik-su bağlantılı karavan parselleri, çadır alanları ve kiralık karavanlarımızla doğa tatilini konforlu hale getiriyoruz. Duş-WC blokları, çamaşırhane, market ve kafemizle tam donanımlı bir kamp deneyimi sunuyoruz. İstanbul'a 1.5 saat mesafede.",
    amenities: ["Ücretsiz WiFi", "Otopark", "Restoran", "Çamaşırhane", "Bisiklet Kiralama", "Çocuk Oyun Alanı"],
    checkIn: "12:00",
    checkOut: "11:00",
    cancellationPolicy: "Girişten 24 saat öncesine kadar ücretsiz iptal",
    languages: ["Türkçe", "İngilizce"],
    status: "approved",
    appliedAt: "2026-04-03T10:00:00Z",
    lat: 40.6812,
    lng: 30.2534,
    images: [],
    membershipTier: "gold",
    goldSince: "2026-03-10T00:00:00Z",
    goldUntil: "2027-03-10T00:00:00Z",
    viewCount: 1654,
    addedAt: "2026-04-03T10:00:00Z",
    trending: true,
  },
  {
    id: "agva-nehir-kamp-alani",
    businessType: "karavan",
    businessName: "Ağva Nehir Kamp Alanı",
    ownerName: "Zeynep Kara",
    email: "info@agvakamp.com",
    phone: "0216 721 44 55",
    city: "İstanbul",
    district: "Şile / Ağva",
    address: "Yeşilçay Mah. Nehir Kenarı No:5, Ağva, Şile, İstanbul",
    roomCount: "50",
    priceRange: "₺ (500-1000₺)",
    instagram: "@agvakamp",
    shortDesc: "İstanbul'a 1 saat, Göksu ve Yeşilçay nehirlerinin buluştuğu noktada kamp alanı.",
    description: "İstanbul'un en yakın doğa kaçamağı Ağva'da, iki nehrin buluştuğu eşsiz noktada kamp yapın. Çadır alanları, bungalov çadırlar ve karavan parselleriyle her bütçeye uygun seçenekler sunuyoruz. Kano, SUP ve nehir yüzme aktiviteleriyle dolu bir hafta sonu sizi bekliyor.",
    amenities: ["Otopark", "Bisiklet Kiralama", "Çocuk Oyun Alanı", "Evcil Hayvan Kabul"],
    checkIn: "13:00",
    checkOut: "11:00",
    cancellationPolicy: "Girişten 24 saat öncesine kadar ücretsiz iptal",
    languages: ["Türkçe"],
    status: "approved",
    appliedAt: "2026-03-15T09:00:00Z",
    lat: 41.0833,
    lng: 29.8167,
    images: [],
    membershipTier: "free",
    viewCount: 1122,
    addedAt: "2026-03-15T09:00:00Z",
  },
  {
    id: "karadeniz-sahil-kamp",
    businessType: "karavan",
    businessName: "Karadeniz Sahil Kamp",
    ownerName: "Bülent Arslan",
    email: "info@karadenizcamp.com",
    phone: "0374 411 22 33",
    city: "Zonguldak",
    district: "Ereğli",
    address: "Sahil Cad. No:18, Ereğli, Zonguldak",
    roomCount: "40",
    priceRange: "₺ (500-1000₺)",
    instagram: "@karadenizcamp",
    shortDesc: "Karadeniz'in serin sularına sıfır, çam ormanları içinde kamp ve karavan alanı.",
    description: "Karadeniz kıyısında, çam ormanlarının gölgesinde kamp yapmanın keyfini çıkarın. Denize sıfır konumuyla sabah yüzme, akşam ateş başında balık keyfi yapabileceğiniz bu kamp alanı, doğa severler için vazgeçilmez bir adres.",
    amenities: ["Otopark", "Çamaşırhane", "Evcil Hayvan Kabul", "Bisiklet Kiralama"],
    checkIn: "12:00",
    checkOut: "10:00",
    cancellationPolicy: "Girişten 24 saat öncesine kadar ücretsiz iptal",
    languages: ["Türkçe"],
    status: "approved",
    appliedAt: "2026-03-10T08:00:00Z",
    lat: 41.2667,
    lng: 31.4167,
    images: [],
    membershipTier: "free",
    viewCount: 678,
    addedAt: "2026-03-10T08:00:00Z",
  },
  {
    id: "seed-6",
    businessType: "firma",
    businessName: "Türkiye Tur & Travel",
    ownerName: "Emre Şahin",
    email: "info@turkiyeturtravel.com",
    phone: "0212 555 77 88",
    city: "İstanbul",
    district: "Sultanahmet",
    address: "Sultanahmet Mah. Divan Yolu Cad. No:22, İstanbul",
    priceRange: "₺₺₺ (2500-5000₺)",
    website: "https://turkiyeturtravel.com",
    instagram: "@turkiyeturtravel",
    shortDesc: "İstanbul merkezli, Türkiye genelinde kültür ve doğa turları düzenleyen turizm firması.",
    description: "15 yıllık deneyimimizle Türkiye'nin dört bir yanında özel ve grup turları düzenliyoruz. Kapadokya balon turları, Ege tekne turları, Güneydoğu kültür rotaları ve daha fazlası için profesyonel rehberlik hizmetimizden yararlanın. TÜRSAB üyesiyiz.",
    amenities: ["Transfer Hizmeti"],
    cancellationPolicy: "Tur başlangıcından 7 gün öncesine kadar ücretsiz iptal",
    languages: ["Türkçe", "İngilizce", "Almanca", "Rusça", "Arapça"],
    status: "approved",
    appliedAt: "2026-03-01T09:00:00Z",
    lat: 41.0082,
    lng: 28.9784,
    images: [],
  },
];
